// =============================================================================
// insuredocsChannel (5.17.0) — same-origin messages between the InsureDocs tab (Main) and the
// standalone HealthSherpa tab, over BroadcastChannel('insuredocs'). Only ids travel — never names or
// other client data. Every call is wrapped so a browser without BroadcastChannel degrades gracefully
// (posts become no-ops; requestOpenClient resolves false and the caller falls back to a new tab).
//
// Messages:
//   {type: 'open-client', clientId, nonce}      HealthSherpa -> Main: open that client's folder
//   {type: 'open-client-ack', nonce}            Main -> HealthSherpa: received (sent before any confirm)
//   {type: 'clients-changed', clientIds: [..]}  HealthSherpa -> Main: refresh the cabinet list + those trees
// =============================================================================

export const CHANNEL_NAME = 'insuredocs';
export const MAIN_WINDOW_NAME = 'insuredocs-main';
export const HEALTHSHERPA_WINDOW_NAME = 'insuredocs-healthsherpa';

const ids = (list) => (Array.isArray(list) ? list.map(Number).filter((n) => Number.isInteger(n) && n > 0) : []);

function open() {
  try {
    return typeof BroadcastChannel === 'undefined' ? null : new BroadcastChannel(CHANNEL_NAME);
  } catch (_e) {
    return null;
  }
}

export function post(msg) {
  const ch = open();
  if (!ch) return false;
  try {
    ch.postMessage(msg);
    return true;
  } catch (_e) {
    return false;
  } finally {
    setTimeout(() => { try { ch.close(); } catch (_e) { /* already closed */ } }, 1000);
  }
}

export function postClientsChanged(clientIds) {
  const list = ids(clientIds);
  return list.length ? post({ type: 'clients-changed', clientIds: list }) : false;
}

// Resolves true if an InsureDocs tab acknowledged within timeoutMs, else false.
export function requestOpenClient(clientId, timeoutMs = 800) {
  const id = Number(clientId);
  return new Promise((resolve) => {
    const ch = open();
    if (!ch || !Number.isInteger(id) || id <= 0) { resolve(false); return; }
    const nonce = `${Date.now()}-${Math.random().toString(36).slice(2)}`;
    let done = false;
    const finish = (ok) => {
      if (done) return;
      done = true;
      try { ch.close(); } catch (_e) { /* already closed */ }
      resolve(ok);
    };
    ch.onmessage = (e) => {
      const m = e && e.data;
      if (m && m.type === 'open-client-ack' && m.nonce === nonce) finish(true);
    };
    try {
      ch.postMessage({ type: 'open-client', clientId: id, nonce });
    } catch (_e) {
      finish(false);
      return;
    }
    setTimeout(() => finish(false), timeoutMs);
  });
}

// Main: listen for HealthSherpa messages. handler(msg) gets only validated, id-only messages.
// Returns an unsubscribe function.
export function subscribe(handler) {
  const ch = open();
  if (!ch) return () => {};
  ch.onmessage = (e) => {
    const m = e && e.data;
    if (!m || typeof m !== 'object') return;
    if (m.type === 'open-client' && Number.isInteger(m.clientId) && m.clientId > 0 && typeof m.nonce === 'string') {
      try { ch.postMessage({ type: 'open-client-ack', nonce: m.nonce }); } catch (_e) { /* the other tab falls back */ }
      handler({ type: 'open-client', clientId: m.clientId });
    } else if (m.type === 'clients-changed') {
      const list = ids(m.clientIds);
      if (list.length) handler({ type: 'clients-changed', clientIds: list });
    }
  };
  return () => { try { ch.close(); } catch (_e) { /* already closed */ } };
}
